import { useContext, useEffect, useState } from 'react';
import { AlbumAddContentTypes } from 'common/enums';
import { ContentEntry } from 'common/album-detail-modal/components/content-entry';
import { UploadVideos } from 'common/camera-upload/components/upload-videos';
import { EntryMetaFields } from 'common/entry-meta-fields';
import { OutOfStorageModal } from 'common/out-of-storage-modal';
import { RecordEntry } from 'common/record-entry';
import { isOutOfStorage } from 'helpers/storage';
import { CenterModal } from 'pages/album-view-mode/components/center-modal';
import { AppContext } from 'context/app-context';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { EntryMeta } from 'context/self-recording-context/context-reducer';
import styles from './new-entry-modal.module.scss';

export interface AlbumOption {
  id: number;
  name: string;
}

interface NewEntryModalProps {
  // Which creator to show: write (note) / audio / video / upload.
  mode: AlbumAddContentTypes;
  vault: VaultType;
  // Albums the entry can be added to (the user's albums in this vault).
  albums: AlbumOption[];
  // Pre-selected album (e.g. the album currently being viewed).
  defaultAlbumId?: number;
  onClose: () => void;
}

// Opens a per-type creator (note editor / recorders / uploader) in a modal from
// the "New" menu. The user picks the target album (or none) and can set the new
// memory's date and location, which are the memory's own: the album keeps its
// own (set on the album page).
export const NewEntryModal = ({
  mode,
  vault,
  albums,
  defaultAlbumId,
  onClose,
}: NewEntryModalProps) => {
  const { dispatch } = useContext(SelfRecordingContext);
  const { state } = useContext(AppContext);
  const { user } = state.data;

  // File-producing entries are blocked when the vault's storage plan is full.
  // Notes are text-only and stay allowed.
  const producesFile = mode !== AlbumAddContentTypes.write;
  const blockedByStorage = producesFile && isOutOfStorage(vault);

  // 0 means "no album": the entry is created unfiled and only shows up in the
  // memory lists. Nothing is pre-selected unless the caller passed an album
  // (e.g. the album currently open).
  const initialAlbumId =
    defaultAlbumId && albums.some((a) => a.id === defaultAlbumId)
      ? defaultAlbumId
      : 0;
  const [selectedAlbumId, setSelectedAlbumId] = useState(initialAlbumId);
  const [meta, setMeta] = useState<EntryMeta>({});

  // The creators read the picked date/location from the recording context and
  // save it on the memory they create. Cleared when the dialog closes, so
  // later uploads elsewhere don't inherit it.
  useEffect(() => {
    dispatch({ type: selfRecordingActions.setEntryMeta, entryMeta: meta });
  }, [dispatch, meta]);
  useEffect(
    () => () =>
      dispatch({ type: selfRecordingActions.setEntryMeta, entryMeta: {} }),
    [dispatch],
  );

  useEffect(() => {
    dispatch({ type: selfRecordingActions.setVaultId, vaultId: vault.id });
    dispatch({
      type: selfRecordingActions.setAlbumId,
      albumId: selectedAlbumId,
    });
    if (mode !== AlbumAddContentTypes.upload) {
      dispatch({ type: selfRecordingActions.setMode, mode });
    }
  }, [dispatch, vault.id, selectedAlbumId, mode]);

  const renderCreator = () => {
    if (mode === AlbumAddContentTypes.upload) {
      return <UploadVideos vault={vault} isEmptyState onDone={onClose} />;
    }
    // Audio/video: record → preview → Delete or Save (memory created on Save,
    // in the background, after the modal closes).
    if (
      mode === AlbumAddContentTypes.audio ||
      mode === AlbumAddContentTypes.video
    ) {
      return (
        <RecordEntry
          kind={mode}
          vaultId={vault.id}
          albumId={selectedAlbumId}
          onDone={onClose}
        />
      );
    }
    // Notes keep the rich-text editor flow.
    return (
      <ContentEntry
        userName={user?.name || ''}
        contentType={mode}
        isCreatingNewContent
        onStopRecording={onClose}
      />
    );
  };

  if (blockedByStorage) {
    return <OutOfStorageModal vault={vault} onClose={onClose} />;
  }

  return (
    <CenterModal onClose={onClose} className={styles.modal} fullScreenOnPhone>
      <div className={styles.body}>
        <span className={styles.title}>Add Entry</span>

        <div className={styles.field}>
          <span className={styles.label}>Album</span>
          <select
            className={styles.select}
            value={selectedAlbumId}
            onChange={(e) => setSelectedAlbumId(Number(e.target.value))}
          >
            <option value={0}>No album</option>
            {albums.map((album) => (
              <option key={album.id} value={album.id}>
                {album.name || 'Untitled'}
              </option>
            ))}
          </select>
        </div>

        {/* The new memory's own date and location, saved with it. */}
        <EntryMetaFields
          location={meta.location}
          startDate={meta.startDate}
          endDate={meta.endDate}
          onDateChange={(startDate, endDate) =>
            setMeta((prev) => ({ ...prev, startDate, endDate }))
          }
          onLocationChange={(location) =>
            setMeta((prev) => ({ ...prev, location }))
          }
        />

        <div className={styles.creator}>{renderCreator()}</div>
      </div>
    </CenterModal>
  );
};
